import { assetsApi } from '@/api/assets';
import { contractsApi } from '@/api/contracts';
import { notificationsApi } from '@/api/notifications';
import { mobilityApi } from '@/api/mobility';

/** Identifies which application area a global search result belongs to. */
export type GlobalSearchPage = 'assets' | 'contracts' | 'documents' | 'notifications' | 'mobility';

/** A single normalized global search hit, ready for display and navigation. */
export interface GlobalSearchResult {
  key: string;
  page: GlobalSearchPage;
  title: string;
  description: string;
  path: string;
}

/**
 * Search assets, contracts, documents, notifications, and mobility employees in parallel
 * and merge the results into a single flat, navigable list.
 * @param search - Free-text search term (must be at least 2 characters after trimming).
 * @returns Up to 5 results per category, or an empty array for short/empty search terms.
 * @throws If every underlying search endpoint fails.
 */
export async function searchAcrossApplication(search: string): Promise<GlobalSearchResult[]> {
  const term = search.trim();
  if (term.length < 2) return [];

  const [assetsResult, contractsResult, documentsResult, notificationsResult, mobilityResult] = await Promise.allSettled([
    assetsApi.list({ search: term, page: 1, pageSize: 5 }),
    contractsApi.list({ search: term, page: 1, pageSize: 5 }),
    contractsApi.searchDocuments({ search: term, page: 1, pageSize: 5 }),
    notificationsApi.list({ search: term, limit: 5 }),
    mobilityApi.listEmployeeMobilityData(new Date().getFullYear()),
  ]);

  // Only fail hard if every source is unavailable; otherwise degrade gracefully
  // and show results from whichever endpoints succeeded.
  if ([assetsResult, contractsResult, documentsResult, notificationsResult, mobilityResult].every((result) => result.status === 'rejected')) {
    throw new Error('Global search endpoints are unavailable.');
  }

  const results: GlobalSearchResult[] = [];

  if (assetsResult.status === 'fulfilled') {
    results.push(...assetsResult.value.items.map((asset) => ({
      key: `asset-${asset.id}`,
      page: 'assets' as const,
      title: asset.assetTag,
      description: [asset.manufacturer, asset.model].filter(Boolean).join(' · '),
      path: `/assets?focusAssetId=${asset.id}`,
    })));
  }

  if (contractsResult.status === 'fulfilled') {
    results.push(...contractsResult.value.items.map((contract) => ({
      key: `contract-${contract.id}`,
      page: 'contracts' as const,
      title: contract.title,
      description: contract.supplierName,
      path: `/contracts/all?focusContractId=${contract.id}`,
    })));
  }

  if (documentsResult.status === 'fulfilled') {
    results.push(...documentsResult.value.results.items.map((document) => ({
      key: `document-${document.contractId}-${document.documentId}`,
      page: 'documents' as const,
      title: document.originalFileName,
      description: `${document.supplierName} · ${document.contractTitle}`,
      path: `/contracts/documents?search=${encodeURIComponent(term)}`,
    })));
  }

  if (notificationsResult.status === 'fulfilled') {
    results.push(...notificationsResult.value.map((notification) => ({
      key: `notification-${notification.notificationEventId}`,
      page: 'notifications' as const,
      title: notification.title,
      description: notification.message,
      path: '/notifications',
    })));
  }

  if (mobilityResult.status === 'fulfilled') {
    // Mobility has no server-side search endpoint; filter the already-fetched
    // employee list client-side across the fields shown in the UI.
    const normalized = term.toLocaleLowerCase('nl');
    results.push(...mobilityResult.value
      .filter((employee) => [employee.fullName, employee.email, employee.currentCategory, employee.absoluteCategory, employee.currentVehicleCategory]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase('nl').includes(normalized)))
      .slice(0, 5)
      .map((employee) => ({
        key: `mobility-employee-${employee.employeeId}`,
        page: 'mobility' as const,
        title: employee.fullName,
        description: [employee.email, employee.currentCategory, employee.currentVehicleCategory].filter(Boolean).join(' · '),
        path: `/mobility/accounts?focusEmployeeId=${employee.employeeId}`,
      })));
  }

  return results;
}
